/**
 * `Modal` — ported from `Foyer_Dashboard_CURRENT.jsx` line 374.
 * Generic dialog with optional title / subtitle, four widths.
 *
 * Backdrop close policy (founder 2026-10-23):
 *   - `closeOnBackdrop` (default `true`) controls whether a mouse click
 *     on the dim backdrop closes the dialog. Set to `false` for form
 *     dialogs so a user's edits can't be lost by a stray backdrop click
 *     (e.g. drag-selecting text that releases outside the panel).
 *   - Even with `closeOnBackdrop={true}`, a mouse-down that *started*
 *     inside the dialog never closes it on mouse-up outside. This
 *     handles the drag-select-and-release-on-backdrop case universally
 *     — the backdrop only closes when the user genuinely began the
 *     click on the backdrop.
 *   - Escape always closes (`closeOnEscape={true}`); flip to `false` on
 *     a dialog that owns a custom unsaved-work prompt.
 *
 * Dirty-form Escape guard (founder 2026-10-23 — 2nd report):
 *   - `dirty` (default `false`) tells the Modal that the body has
 *     unsaved edits. When Escape fires on a dirty dialog, Modal shows
 *     an inner "Discard changes?" confirm instead of calling `onClose`
 *     immediately. "Discard" calls `onClose`; "Keep editing" dismisses
 *     the confirm. A clean dialog (`dirty={false}`) closes on Escape
 *     exactly as before.
 *   - The X button and backdrop click (where enabled) are NOT
 *     intercepted — a user who clicks "cancel-looking" affordances is
 *     making a deliberate choice. Only the keyboard escape, which was
 *     accidentally discarding edits, prompts.
 */

import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  size?: ModalSize;
  title?: ReactNode;
  subtitle?: ReactNode;
  /**
   * When `true` (default), a plain click on the dim backdrop closes
   * the dialog. Set to `false` for form dialogs so a stray outside
   * click can't discard unsaved edits.
   */
  closeOnBackdrop?: boolean;
  /** When `true` (default), the Escape key closes the dialog. */
  closeOnEscape?: boolean;
  /**
   * When `true`, Escape on this dialog opens an inner "Discard changes?"
   * confirm instead of closing immediately. Set from the hosting form
   * when a user-editable field diverges from its initial value.
   * `false` (default) keeps the current Escape → `onClose` behavior.
   */
  dirty?: boolean;
}

const widths: Record<ModalSize, number> = {
  sm: 460,
  md: 620,
  lg: 880,
  xl: 1100,
};

export function Modal({
  open,
  onClose,
  children,
  size = 'md',
  title,
  subtitle,
  closeOnBackdrop = true,
  closeOnEscape = true,
  dirty = false,
}: ModalProps): JSX.Element | null {
  const C = useC();
  // Track where mouse-down started so a drag-select that releases
  // outside the panel never closes it. `null` → the current gesture
  // is backdrop-rooted; a non-null panel ref means it started inside.
  const panelRef = useRef<HTMLDivElement | null>(null);
  const mouseDownOnPanelRef = useRef(false);
  // When Escape hits a `dirty` dialog, show an inner confirm panel
  // instead of unmounting right away. Reset when the dialog closes.
  const [discardOpen, setDiscardOpen] = useState(false);

  // Escape → onClose, or (if dirty) → open the Discard confirm.
  // Listens on window so it fires even when focus sits inside a form
  // field. We read `dirty` through a ref inside the listener so a
  // mid-edit toggle (dirty flips from false to true) doesn't skip
  // the guard — React will re-run the effect, but the ref write is
  // the authoritative read for the pending keystroke.
  const dirtyRef = useRef(dirty);
  useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);
  // Reset the discard-confirm panel when the dialog closes so a
  // re-opened dialog starts clean. (The parent conditionally renders
  // Modal from `open`, so the full-unmount path handles that already;
  // this covers the case where `open` toggles via internal state.)
  useEffect(() => {
    if (!open) setDiscardOpen(false);
  }, [open]);
  useEffect(() => {
    if (!open || !closeOnEscape) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (dirtyRef.current) {
        setDiscardOpen(true);
      } else {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closeOnEscape, onClose]);

  if (!open) return null;
  // ESLint-suppression-equivalent: mono not used outside title clause; left
  // imported for future label rows ported into the modal body.
  void monoFont;

  const onBackdropMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    // The backdrop is the outer motion.div; if the target IS the backdrop
    // (not a bubbled child), this gesture started on the backdrop and may
    // legitimately close on mouse-up.
    mouseDownOnPanelRef.current =
      panelRef.current !== null
      && e.target instanceof Node
      && panelRef.current.contains(e.target);
  };

  const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    // Only close when (a) backdrop close is enabled, (b) the click
    // target was the backdrop itself, and (c) the mouse-down did NOT
    // start inside the dialog (never close a drag-select release).
    if (!closeOnBackdrop) return;
    if (mouseDownOnPanelRef.current) {
      mouseDownOnPanelRef.current = false;
      return;
    }
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)' }}
      onMouseDown={onBackdropMouseDown}
      onClick={onBackdropClick}
      role="dialog"
      aria-modal="true"
    >
      <motion.div
        ref={panelRef}
        initial={{ scale: 0.96, opacity: 0, y: 40 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.96, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full overflow-hidden flex flex-col relative"
        style={{
          background: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: 4,
          maxWidth: widths[size],
          maxHeight: '92vh',
        }}
      >
        {(title || subtitle) && (
          <div
            className="flex items-start justify-between px-6 py-4 flex-shrink-0"
            style={{ borderBottom: `1px solid ${C.border}`, background: C.surface }}
          >
            <div className="flex-1 min-w-0">
              {title ? (
                <div
                  style={{
                    fontFamily: serifFont,
                    fontSize: 22,
                    color: C.text,
                    letterSpacing: -0.5,
                    lineHeight: 1.1,
                  }}
                >
                  {title}
                </div>
              ) : null}
              {subtitle ? (
                <div
                  style={{
                    fontFamily: sansFont,
                    fontSize: 12,
                    color: C.muted,
                    marginTop: 4,
                  }}
                >
                  {subtitle}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close dialog"
              className="flex items-center justify-center flex-shrink-0 ml-3"
              style={{
                width: 30,
                height: 30,
                background: C.surface2,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
              }}
            >
              <X size={14} color={C.muted} />
            </button>
          </div>
        )}
        {/* overflow-x-hidden: a child wider than the panel (long row, chip run)
            must never push a horizontal page scroll on narrow/mobile screens. */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">{children}</div>
        {discardOpen ? (
          <DiscardChangesConfirm
            C={C}
            onKeepEditing={() => setDiscardOpen(false)}
            onDiscard={() => {
              setDiscardOpen(false);
              onClose();
            }}
          />
        ) : null}
      </motion.div>
    </motion.div>
  );
}

/**
 * Inner "Discard changes?" confirm shown when Escape fires on a dirty
 * dialog. Rendered inside the Modal panel (not as a separate dialog
 * overlay) so it visually sits ON the form the user was editing — the
 * form is still there, the question is just "do you really mean to
 * close me?". Uses the same density tokens as the Modal body: thin
 * 1px borders, 2px radii, generous whitespace.
 */
function DiscardChangesConfirm({
  C,
  onKeepEditing,
  onDiscard,
}: {
  readonly C: ReturnType<typeof useC>;
  readonly onKeepEditing: () => void;
  readonly onDiscard: () => void;
}): JSX.Element {
  const keepRef = useRef<HTMLButtonElement | null>(null);
  // Focus "Keep editing" when the confirm opens — the safe default,
  // and Enter on the keyboard then keeps the user in the form rather
  // than discarding by accident.
  useEffect(() => {
    keepRef.current?.focus();
  }, []);
  return (
    <div
      role="alertdialog"
      aria-label="Discard changes?"
      className="absolute inset-0 flex items-end sm:items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }}
      // The outer click handler shouldn't bubble through to the main
      // Modal backdrop — this confirm owns its own gestures.
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="w-full max-w-sm p-5"
        style={{
          background: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: 4,
        }}
      >
        <div
          style={{
            fontFamily: serifFont,
            fontSize: 22,
            color: C.text,
            letterSpacing: -0.5,
            lineHeight: 1.15,
            marginBottom: 6,
          }}
        >
          Discard changes?
        </div>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.muted,
            lineHeight: 1.5,
            marginBottom: 14,
          }}
        >
          You have unsaved edits in this form.
        </div>
        <div className="flex flex-col-reverse sm:flex-row items-stretch gap-2">
          <button
            ref={keepRef}
            type="button"
            onClick={onKeepEditing}
            data-testid="modal-discard-keep"
            className="w-full sm:w-auto px-4 py-2"
            style={{
              background: 'transparent',
              border: `1px solid ${C.border}`,
              color: C.muted,
              fontFamily: sansFont,
              fontSize: 13,
              borderRadius: 2,
            }}
          >
            Keep editing
          </button>
          <button
            type="button"
            onClick={onDiscard}
            data-testid="modal-discard-confirm"
            className="w-full sm:flex-1 px-4 py-2"
            style={{
              background: C.coral,
              color: '#FFFFFF',
              fontFamily: sansFont,
              fontSize: 13,
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            Discard
          </button>
        </div>
      </div>
    </div>
  );
}
